import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "../../component/layout";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { useFacilityStore } from "../../../../app/z-store/store";
import { updatePurchaseRequestURL, getPurchaseRequestUrl } from "../../../../utils/urls";

const EditPurchaseRequest = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const facilityId = useFacilityStore(state => state.facilityId);

  const [formValues, setFormValues] = useState({
    irfNumber: '',
    date: '',
    from: '',
    department: '',
    items: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (facilityId && requestId) {
      fetchData();
    }
  }, [facilityId, requestId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await makeRequest2(
        `${getPurchaseRequestUrl}/${facilityId}/${requestId}`,
        'GET'
      );
      if (res.success && res.data) {
        const data = res.data.data;
        setFormValues({
          irfNumber: data.irfNumber || '',
          date: data.date ? data.date.split('T')[0] : '',
          from: data.from || '',
          department: data.department || '',
          items: data.items || []
        });
      } else {
        toastify('Failed to load RFQ details', 'error');
      }
    } catch (err) {
      console.error(err);
      toastify('Error fetching RFQ', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formValues.items];
    newItems[index][field] = value;
    setFormValues(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    setFormValues(prev => ({
      ...prev,
      items: [...prev.items, { itemDescription: '', quantity: '', unitOfMeasure: '', remarksSpecification: '' }]
    }));
  };

  const removeItem = (index) => {
    const newItems = formValues.items.filter((_, i) => i !== index);
    setFormValues(prev => ({ ...prev, items: newItems }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...formValues };
      const res = await makeRequest2(
        `${updatePurchaseRequestURL}/${facilityId}/${requestId}`,
        'PUT',
        payload
      );
      if (res.success) {
        toastify('RFQ updated successfully', 'success');
      } else {
        toastify(res.error || 'Failed to update RFQ', 'error');
      }
    } catch (err) {
      console.error(err);
      toastify('Error updating RFQ', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="page-header">
        <ul className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
          <li className="breadcrumb-item"><Link to="/facility/procurement_management/purchase_requests">Purchase Requests</Link></li>
          <li className="breadcrumb-item">Edit RFQ</li>
        </ul>
      </div>

      <div className="card mb-4">
        <div className="card-body d-flex align-items-center">
          <Link to="/facility/procurement_management/purchase_requests" className="me-2"> 
          <i className="ti ti-arrow-narrow-left"></i> Back
          </Link>
          
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h5>Edit RFQ</h5></div>
        <div className="card-body">
          {loading
            ? <div className="d-flex justify-content-center"><div className="spinner-border" role="status"></div></div>
            : <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">IRF Number</label>
                  <input
                    type="text"
                    className="form-control"
                    name="irfNumber"
                    value={formValues.irfNumber}
                    onChange={handleChange}
                    required
                    readOnly
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="date"
                    value={formValues.date}
                    onChange={handleChange}
                    required
                    readOnly
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Requested By</label>
                  <input
                    type="text"
                    className="form-control"
                    name="from"
                    value={formValues.from}
                    onChange={handleChange}
                    required
                    readOnly
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Department</label>
                  <input
                    type="text"
                    className="form-control"
                    name="department"
                    value={formValues.department}
                    onChange={handleChange}
                    required
                    readOnly
                  />
                </div>

                <hr />
                <h6>Items</h6>
                {formValues.items.map((item, idx) => (
                  <div key={idx} className="row mb-2 align-items-center">
                    <div className="col">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Description"
                        value={item.itemDescription}
                        onChange={e => handleItemChange(idx, 'itemDescription', e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-2">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={e => handleItemChange(idx, 'quantity', e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Unit"
                        value={item.unitOfMeasure}
                        onChange={e => handleItemChange(idx, 'unitOfMeasure', e.target.value)}
                      />
                    </div>
                    <div className="col">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Remarks"
                        value={item.remarksSpecification}
                        onChange={e => handleItemChange(idx, 'remarksSpecification', e.target.value)}
                      />
                    </div>
                    <div className="col-auto">
                      <button type="button" className="btn btn-danger" onClick={() => removeItem(idx)}>×</button>
                    </div>
                  </div>
                ))}
                <button type="button" className="btn btn-outline-primary mb-3" onClick={addItem}>
                  Add Item
                </button>

                <div className="d-flex justify-content-end">
                  <button type="button" className="btn btn-secondary me-2" onClick={() => navigate(-1)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save Changes
                  </button>
                </div>
              </form>
          }
        </div>
      </div>
    </Layout>
  );
};

export default EditPurchaseRequest;
